import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// A single glanceable stat: a big tabular-nums value over a quiet label. The
// value-on-top idiom lifted out of WeeklyStatsCard when its two numbers folded
// into the Home meter header (AP-1b-u) — one place for "big number + caption"
// so a stat row can't drift in weight/spacing between callsites.
//
// ProgressView keeps its own label-on-TOP variant with an inline danger tint
// (a different orientation + tone) — reconciling the two under one prop-driven
// StatPair is tracked as CN-statpair in next/consolidation.md, not forced here.
type Props = {
  theme: UiTheme;
  value: string;
  label: string;
  // Override the value colour (e.g. a zone tint). Defaults to `ink`.
  valueColor?: string;
  // How many lines the label may wrap to before truncating (default 1). The
  // coach KPI strip passes 2 so long captions (DE/RU "Benötigen Aufmerksamkeit"
  // / "Тренировки на этой неделе") stay legible instead of ellipsizing away the
  // only context for the number.
  labelLines?: number;
};

export function StatPair({
  theme,
  value,
  label,
  valueColor,
  labelLines = 1,
}: Props) {
  const styles = makeStyles(theme);
  return (
    <View style={styles.stat}>
      <Text
        style={[styles.value, valueColor ? { color: valueColor } : null]}
        numberOfLines={1}
      >
        {value}
      </Text>
      <Text style={styles.label} numberOfLines={labelLines}>
        {label}
      </Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t } = theme;
  return StyleSheet.create({
    stat: { gap: 2 },
    value: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
      letterSpacing: -0.3,
    },
    label: { color: p.ink2, fontSize: t.sizes.sm },
  });
}
